import React from 'react';
import { Link } from '@/lib/router';
import { 
  ArrowRight, Zap, Wind, Activity, Car, 
  ChevronRight, CheckCircle, Award,
  Users, Headphones,
  Smartphone, Cpu, Camera, Video
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import SEO from '@/components/SEO';
import { useQuoteModal } from '@/contexts/QuoteModalContext';
import { products, productCategories } from '@/data/products';
import { applicationAreas } from '@/data/site';

const Home: React.FC = () => {
  const { openModal } = useQuoteModal();
  
  const featuredProducts = products.slice(0, 4);
  const newProducts = products.filter(p => p.isNew);

  return (
    <>
      <SEO
        title="KeyGoTech - Advanced Acoustic Imaging Solutions"
        description="Leading manufacturer of acoustic imagers, acoustic cameras, and NVH testing equipment. Professional solutions for partial discharge detection, gas leak detection, and industrial inspection."
        keywords={['acoustic imager', 'acoustic camera', 'NVH testing', 'partial discharge detection', 'gas leak detection', 'industrial inspection']}
      />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#002a55] via-[#00376d] to-[#002a55] text-white overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }} />
        </div>

        <div className="container mx-auto px-4 py-20 lg:py-32 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <Badge className="bg-amber-500 text-white border-0">
                Industry Leading Technology
              </Badge>
              <h1 className="text-4xl lg:text-6xl font-bold leading-tight">
                See Sound.
                <br />
                <span className="text-amber-400">Solve Problems.</span>
              </h1>
              <p className="text-xl text-[#e6f0f9] max-w-xl">
                Advanced acoustic imaging solutions for industrial inspection, NVH testing, and traffic monitoring. Transform invisible sound into actionable visual insights.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  size="lg"
                  className="bg-amber-500 hover:bg-amber-600 text-white"
                  asChild
                >
                  <Link to="/products">
                    Explore Products
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Link>
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white text-black hover:bg-white/90  "
                  onClick={() => openModal()}
                >
                  Request Quote
                </Button>
              </div>
              <div className="flex items-center gap-6 pt-4">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span className="text-sm">136-256 Channels</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span className="text-sm">Real-time Imaging</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span className="text-sm">IP54-67 Rated</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="bg-[#00376d]/50 rounded-2xl overflow-hidden border border-[#00376d]">
                <img
                  src="/Img/EN/超声声学成像仪banner英文-c.jpg"
                  alt="KeyGo Acoustic Imager in action"
                  className="w-full h-full object-cover object-center"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = 'https://placehold.co/800x800/1e3a8a/ffffff?text=Acoustic+Imager+Hero';
                  }}
                />
              </div>
              {/* Floating Stats */}
               {/*<div className="absolute -bottom-6 -left-6 bg-white text-slate-900 p-4 rounded-xl shadow-xl">
                <div className="text-3xl font-bold text-[#00376d]">500+</div>
                <div className="text-sm text-slate-600">Global Customers</div>
              </div>*/}
              <div className="absolute -top-6 -right-6 bg-amber-500 text-white p-4 rounded-xl shadow-xl">
                <div className="text-3xl font-bold">15+</div>
                <div className="text-sm">Years Experience</div>
              </div>
            </div>
          </div>
        </div>

        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#f8fafc"/>
          </svg>
        </div>
      </section>

      {/* Product Categories */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Product Categories
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Comprehensive range of acoustic imaging solutions designed for diverse industrial applications
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {productCategories.slice(0, 4).map((category) => (
              <Link
                key={category.id}
                to={category.id === 'handheld' ? '/products/handheld' : `/products/${category.id}`}
                className="group bg-white rounded-xl border border-slate-200 p-6 hover:shadow-xl hover:border-[#b3d1e8] transition-all"
              >
                <div className="w-14 h-14 bg-[#e6f0f9] rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#00376d] transition-colors">
                  {category.id === 'handheld' && <Smartphone className="w-7 h-7 text-[#00376d] group-hover:text-white transition-colors" />}
                  {category.id === 'fixed' && <Video className="w-7 h-7 text-[#00376d] group-hover:text-white transition-colors" />}
                  {category.id === 'modules' && <Cpu className="w-7 h-7 text-[#00376d] group-hover:text-white transition-colors" />}
                  {category.id === 'cameras' && <Camera className="w-7 h-7 text-[#00376d] group-hover:text-white transition-colors" />}
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2 group-hover:text-[#00376d] transition-colors">
                  {category.name}
                </h3>
                <p className="text-slate-600 text-sm mb-4">
                  {category.description}
                </p>
                <span className="inline-flex items-center text-[#00376d] text-sm font-medium">
                  Learn More
                  <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            ))}
          </div>
          <div className="text-center mt-8">
            <Button variant="outline" className="border-[#00376d] text-[#00376d] hover:bg-[#e6f0f9]" asChild>
              <Link to="/products">
                View All Categories
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
                Featured Products
              </h2>
              <p className="text-slate-600 max-w-xl">
                Our most popular acoustic imaging solutions trusted by industry leaders worldwide
              </p>
            </div>
            <Button variant="outline" className="border-[#00376d] text-[#00376d] hover:bg-[#e6f0f9] mt-4 md:mt-0" asChild>
              <Link to="/products">
                View All Products
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredProducts.map((product) => (
              <div key={product.id} className="group bg-slate-50 rounded-xl border border-slate-200 overflow-hidden hover:shadow-xl transition-all">
                <Link to={`/products/${product.category}/${product.id}`} className="block">
                  <div className="aspect-[4/3] bg-white overflow-hidden">
                    <img
                      src={product.image}
                      alt={product.model}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = `https://placehold.co/600x450/1e40af/ffffff?text=${encodeURIComponent(product.model)}`;
                      }}
                    />
                  </div>
                </Link>
                <div className="p-5">
                  <div className="text-sm text-[#00376d] font-medium mb-1">{product.model}</div>
                  <h3 className="font-semibold text-slate-900 mb-2">
                    <Link to={`/products/${product.category}/${product.id}`} className="hover:text-[#00376d] transition-colors">
                      {product.name}
                    </Link>
                  </h3>
                  <p className="text-slate-600 text-sm mb-4 line-clamp-2">
                    {product.shortDescription}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full border-[#00376d] text-[#00376d] hover:bg-[#e6f0f9]"
                    asChild
                  >
                    <Link to={`/products/${product.category}/${product.id}`}>
                      Learn More
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </Link>
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* New Products */}
      {newProducts.length > 0 && (
        <section className="py-20 bg-gradient-to-br from-amber-50 to-orange-50">
          <div className="container mx-auto px-4">
            <div className="flex items-center gap-3 mb-8">
              <Badge className="bg-amber-500 text-white">
                NEW RELEASE
              </Badge>
              <h2 className="text-3xl font-bold text-slate-900">
                Latest Innovations
              </h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {newProducts.map((product) => (
                <div key={product.id} className="bg-white rounded-2xl border border-amber-200 p-6 lg:p-8 flex flex-col lg:flex-row gap-6">
                  <div className="lg:w-1/2 aspect-video lg:aspect-square bg-slate-100 rounded-xl overflow-hidden">
                    <img
                      src={product.image}
                      alt={product.model}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = `https://placehold.co/600x600/f59e0b/ffffff?text=${encodeURIComponent(product.model)}`;
                      }}
                    />
                  </div>
                  <div className="lg:w-1/2 flex flex-col justify-center">
                    <div className="text-sm text-amber-600 font-medium mb-1">{product.model}</div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-3">{product.name}</h3>
                    <p className="text-slate-600 mb-6">{product.description}</p>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Button className="bg-amber-500 hover:bg-amber-600 text-white" asChild>
                        <Link to={`/products/${product.category}/${product.id}`}>
                          Learn More
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Link>
                      </Button>
                      <Button variant="outline" className="border-[#00376d] text-[#00376d] hover:bg-[#e6f0f9]" onClick={() => openModal(product.model)}>
                        Request Quote
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Applications */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Application Areas
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Discover how our acoustic imaging solutions solve real-world challenges across industries
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {applicationAreas.map((app) => {
              const IconComponent = app.icon === 'Zap' ? Zap : 
                                   app.icon === 'Wind' ? Wind : 
                                   app.icon === 'Car' ? Car : Activity;
              return (
                <Link
                  key={app.id}
                  to={`/applications/${app.id}`}
                  className="group relative bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-xl transition-all"
                >
                  <div className="aspect-[16/9] bg-slate-100">
                    <img
                      src={app.image}
                      alt={app.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = `https://placehold.co/800x450/1e40af/ffffff?text=${encodeURIComponent(app.title)}`;
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                    <div className="flex items-center gap-2 mb-2">
                      <IconComponent className="w-5 h-5" />
                      <span className="text-sm font-medium opacity-80">Application</span>
                    </div>
                    <h3 className="text-xl font-bold mb-2">{app.title}</h3>
                    <p className="text-white/80 text-sm line-clamp-2">{app.description}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-[#002a55] text-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-8 lg:gap-12">
            <div className="text-center min-w-[150px]">
              <div className="text-4xl lg:text-5xl font-bold text-amber-400 mb-2">20+</div>
              <div className="text-[#b3d1e8]">Product Models</div>
            </div>
            <div className="text-center min-w-[150px]">
              <div className="text-4xl lg:text-5xl font-bold text-amber-400 mb-2">15+</div>
              <div className="text-[#b3d1e8]">Years of Innovation</div>
            </div>
            <div className="text-center min-w-[200px]">
              <div className="text-4xl lg:text-5xl font-bold text-amber-400 mb-2">100%</div>
              <div className="text-[#b3d1e8]">Core Technology R&D</div>
            </div>
            <div className="text-center min-w-[200px]">
              <div className="text-4xl lg:text-5xl font-bold text-amber-400 mb-2">1000+</div>
              <div className="text-[#b3d1e8]">Industrial Customers</div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6">
                Why Choose KeyGoTech?
              </h2>
              <p className="text-slate-600 mb-8">
                We are committed to delivering cutting-edge acoustic imaging technology with exceptional service and support.
              </p>
              <div className="space-y-4">
                {[
                  { icon: Award, title: 'Industry Leading Technology', desc: 'Advanced beamforming algorithms with up to 256 channels' },
                  { icon: Users, title: 'Expert Support Team', desc: 'Dedicated technical support and application guidance' },
                  { icon: Headphones, title: 'Comprehensive Training', desc: 'Product training and certification programs' }
                ].map((item, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="w-12 h-12 bg-[#e6f0f9] rounded-xl flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-6 h-6 text-[#00376d]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{item.title}</h3>
                      <p className="text-slate-600 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className=" bg-slate-100 rounded-2xl">
                <img
                  src="about-company.jpg"
                  alt="KeyGoTech Facility"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = 'https://placehold.co/800x600/1e40af/ffffff?text=Why+Choose+KeyGo';
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="bg-gradient-to-r from-[#00376d] to-[#002a55] rounded-3xl p-8 lg:p-16 text-center text-white">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              Ready to Transform Your Inspection Process?
            </h2>
            <p className="text-[#e6f0f9] max-w-2xl mx-auto mb-8">
              Contact our team today to discuss your requirements and discover how our acoustic imaging solutions can help you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-amber-500 hover:bg-amber-600 text-white"
                onClick={() => openModal()}
              >
                Request a Quote
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-white text-black hover:bg-white/90"
                asChild
              >
                <Link to="/contact">
                  Contact Sales
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
